import { useState } from 'react'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { Check, Crown, CreditCard, ExternalLink, LogOut, Menu, Moon, Store, Sun, User, Wallet } from 'lucide-react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useBillingStatus } from '@/hooks/use-billing-status'
import { useConnectedRozetkaStore, useKmpEligibility } from '@/hooks/use-store-connection'
import { useTheme } from '@/components/theme-provider'
import { useAuthStore, useHeaderUsageLimitsStore } from '@/lib/store'
import apiClient from '@/lib/api-client'
import { notifyExtensionLogout } from '@/lib/extension-auth'
import { MpButton, MpIconButton, MpStatusPill } from '@/ui/components'
import { cn } from '@/lib/utils'

interface DashboardHeaderProps {
  onToggleSidebar?: () => void
}

function formatAmount(amountMinor: number, currency: string) {
  return new Intl.NumberFormat('uk-UA', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(amountMinor / 100)
}

function HeaderCompanionMeerkat({ busy, message }: { busy: boolean; message: string }) {
  const [playful, setPlayful] = useState(false)

  function handleClick() {
    setPlayful(true)
    window.setTimeout(() => setPlayful(false), 900)
  }

  return (
    <button
      type="button"
      className={cn('mph-header-meerkat', busy && 'is-busy', playful && 'is-playful')}
      onClick={handleClick}
      aria-label="Сурикат чергового сканування"
      title={message}
    >
      <span className="mph-header-meerkat__bubble">{playful ? 'бачу' : message}</span>
      <span className="mph-header-meerkat__stage" aria-hidden="true">
        <span className="mph-header-meerkat__figure">
          <span className="mph-header-meerkat__tail" />
          <span className="mph-header-meerkat__body" />
          <span className="mph-header-meerkat__belly" />
          <span className="mph-header-meerkat__head" />
          <span className="mph-header-meerkat__ear mph-header-meerkat__ear--left" />
          <span className="mph-header-meerkat__ear mph-header-meerkat__ear--right" />
          <span className="mph-header-meerkat__snout" />
          <span className="mph-header-meerkat__eye mph-header-meerkat__eye--left" />
          <span className="mph-header-meerkat__eye mph-header-meerkat__eye--right" />
          <span className="mph-header-meerkat__arm mph-header-meerkat__arm--left" />
          <span className="mph-header-meerkat__arm mph-header-meerkat__arm--right" />
          <span className="mph-header-meerkat__foot mph-header-meerkat__foot--left" />
          <span className="mph-header-meerkat__foot mph-header-meerkat__foot--right" />
        </span>
      </span>
    </button>
  )
}

export function DashboardHeader({ onToggleSidebar }: DashboardHeaderProps) {
  const { theme, setTheme } = useTheme()
  const { user, logout } = useAuthStore()
  const navigate = useNavigate()
  const { orgId } = useParams()
  const showMailpit = import.meta.env.DEV
  const mailpitUrl = (import.meta.env as any).VITE_MAILPIT_URL || 'http://localhost:8025'
  const connectedStore = useConnectedRozetkaStore()
  const kmpEligibility = useKmpEligibility()
  const headerCompanion = useHeaderUsageLimitsStore((state) => state.companion)
  const store = connectedStore.data
  const isKmpMember = Boolean(kmpEligibility.data?.is_member)
  const profilePath = orgId ? `/o/${orgId}/profile` : '/profile'
  const billingPath = orgId ? `/o/${orgId}/billing` : '/billing'
  const initials = user?.email.split('@')[0].slice(0, 2).toUpperCase() || 'MP'
  const billingQuery = useBillingStatus({ enabled: Boolean(user), staleTime: 30_000 })
  const entitlement = billingQuery.data?.entitlement
  const planLabel = entitlement
    ? entitlement.is_active
      ? entitlement.plan_name || entitlement.plan
      : 'Тариф не обрано'
    : ''
  const balanceLabel = entitlement?.balance
    ? formatAmount(entitlement.balance.available_minor, entitlement.balance.currency)
    : ''

  async function handleLogout() {
    try {
      await apiClient.post('/api/v1/auth/logout/')
      await notifyExtensionLogout()
    } finally {
      logout()
      navigate('/login')
    }
  }

  return (
    <header className="z-20 flex h-12 shrink-0 items-center gap-2 border-b border-[var(--mph-border-subtle)] bg-[var(--mph-bg-workspace)]/78 px-3 text-[var(--mph-text-primary)] backdrop-blur-xl lg:px-4">
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <MpIconButton
          label="Відкрити навігацію"
          icon={Menu}
          className="lg:hidden"
          onClick={onToggleSidebar}
        />
        {headerCompanion?.visible ? (
          <div className="mph-header-companion-lane hidden min-w-[180px] flex-1 md:block">
            <HeaderCompanionMeerkat busy={headerCompanion.busy} message={headerCompanion.message} />
          </div>
        ) : null}
      </div>
      <div className="flex items-center gap-2">
        {planLabel ? (
          <Link
            to={billingPath}
            className="hidden max-w-[180px] items-center gap-1.5 rounded-[9px] border border-[var(--mph-border-subtle)] bg-[var(--mph-bg-control)] px-2.5 py-1.5 text-xs font-medium text-[var(--mph-text-secondary)] transition-colors hover:bg-[var(--mph-bg-control-hover)] hover:text-[var(--mph-text-primary)] md:inline-flex"
            title="Деталі тарифу"
          >
            <CreditCard className="h-3.5 w-3.5 shrink-0 text-[var(--mph-text-tertiary)]" />
            <span className="min-w-0 truncate">{planLabel}</span>
          </Link>
        ) : null}
        {balanceLabel ? (
          <Link
            to={billingPath}
            className={cn(
              'inline-flex max-w-[150px] items-center gap-1.5 rounded-[9px] border px-2.5 py-1.5 text-xs font-semibold transition-colors sm:max-w-[170px]',
              (entitlement?.balance?.available_minor || 0) > 0
                ? 'border-[var(--mph-accent-border)] bg-[var(--mph-accent-soft)] text-[var(--mph-accent)] hover:brightness-105'
                : 'border-[var(--mph-warning)]/30 bg-[var(--mph-warning-soft)] text-[var(--mph-warning)] hover:brightness-105',
            )}
            title="Баланс організації"
          >
            <Wallet className="h-3.5 w-3.5 shrink-0" />
            <span className="hidden sm:inline">Баланс</span>
            <span className="min-w-0 truncate">{balanceLabel}</span>
          </Link>
        ) : null}
        {store?.has_public_seller_identity && (
          <a
            href={store.url}
            target="_blank"
            rel="noreferrer"
            className="hidden max-w-[240px] items-center gap-2 rounded-[9px] border border-[var(--mph-border)] bg-[var(--mph-bg-control)] px-2.5 py-1.5 text-sm text-[var(--mph-text-primary)] transition-colors hover:bg-[var(--mph-bg-control-hover)] md:inline-flex"
            title={store.name}
          >
            <Store className="h-4 w-4 shrink-0 text-[var(--mph-text-tertiary)]" />
            <span className="min-w-0 truncate font-medium">{store.name}</span>
            {isKmpMember && (
              <MpStatusPill tone="warning" className="shrink-0">
                <Crown className="h-3 w-3" />
                КМП
              </MpStatusPill>
            )}
            <ExternalLink className="h-3.5 w-3.5 shrink-0 text-[var(--mph-text-tertiary)]" />
          </a>
        )}
        {showMailpit && (
          <MpButton size="sm" variant="secondary" asChild>
            <a href={mailpitUrl} target="_blank" rel="noreferrer">
              Mailpit
            </a>
          </MpButton>
        )}
        <DropdownMenu.Root modal={false}>
          <DropdownMenu.Trigger asChild>
            <MpIconButton label="Перемкнути тему">
              <Sun className="h-4 w-4 scale-100 rotate-0 transition-all dark:scale-0 dark:-rotate-90" />
              <Moon className="absolute h-4 w-4 scale-0 rotate-90 transition-all dark:scale-100 dark:rotate-0" />
            </MpIconButton>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content
              align="end"
              className="z-50 min-w-40 rounded-[12px] border border-[var(--mph-border)] bg-[var(--mph-bg-panel-raised)] p-1 text-sm text-[var(--mph-text-primary)] shadow-[var(--mph-shadow-panel)]"
            >
              {[
                ['light', 'Світла'],
                ['dark', 'Темна'],
                ['system', 'Системна'],
              ].map(([value, label]) => (
                <DropdownMenu.Item
                  key={value}
                  onClick={() => setTheme(value as 'light' | 'dark' | 'system')}
                  className="flex cursor-pointer items-center gap-2 rounded-[9px] px-2 py-1.5 outline-none hover:bg-[var(--mph-bg-control)]"
                >
                  {label}
                  <Check className={cn('ml-auto h-3.5 w-3.5', theme !== value && 'opacity-0')} />
                </DropdownMenu.Item>
              ))}
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
        {user ? (
          <DropdownMenu.Root modal={false}>
            <DropdownMenu.Trigger asChild>
              <button
                type="button"
                className="flex h-9 w-9 items-center justify-center rounded-full border border-[var(--mph-border)] bg-[var(--mph-bg-control)] text-xs font-semibold text-[var(--mph-text-primary)] outline-none transition hover:bg-[var(--mph-bg-control-hover)] focus-visible:shadow-[var(--mph-focus-ring)]"
                aria-label="Меню користувача"
              >
                {initials}
              </button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content
                align="end"
                className="z-50 w-64 rounded-[14px] border border-[var(--mph-border)] bg-[var(--mph-bg-panel-raised)] p-1 text-sm text-[var(--mph-text-primary)] shadow-[var(--mph-shadow-panel)]"
              >
                <div className="px-3 py-2">
                  <p className="truncate font-medium">{user.first_name || user.email.split('@')[0]}</p>
                  <p className="truncate text-xs text-[var(--mph-text-tertiary)]">{user.email}</p>
                </div>
                <div className="my-1 h-px bg-[var(--mph-border-subtle)]" />
                <DropdownMenu.Item
                  onClick={() => navigate(profilePath)}
                  className="flex cursor-pointer items-center gap-2 rounded-[9px] px-2 py-1.5 outline-none hover:bg-[var(--mph-bg-control)]"
                >
                  <User className="h-4 w-4" />
                  Профіль
                </DropdownMenu.Item>
                <DropdownMenu.Item
                  onClick={handleLogout}
                  className="flex cursor-pointer items-center gap-2 rounded-[9px] px-2 py-1.5 text-[var(--mph-danger)] outline-none hover:bg-[var(--mph-danger-soft)]"
                >
                  <LogOut className="h-4 w-4" />
                  Вийти
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        ) : null}
      </div>
    </header>
  )
}
